/**
 * ToolsMenu — popover that exposes the composer-level toggles the user
 * can flip *for the next message*.
 *
 * v18 mockup ref: lines ~842-951 (`.composer .tools` button + popover).
 *
 * June 11, 2026 — full visual + IA pass:
 *  - Native ``<input type="checkbox">`` (browser-styled, blue on
 *    Chromium) replaced by the accessible ``Switch`` primitive (WCAG
 *    1.4.11 — orange-soft track + thumb-position + thumb-fill cues).
 *  - Section split "Modalità di ricerca" + "Tipo di risposta" collapsed
 *    into a single flat list. The 3-radio FocusSelector
 *    (Standard/Strutturati/Full) was the SOTA-2026 anti-pattern flagged
 *    by the UX audit: "Strutturati" is now auto-derived from connector
 *    selection in Ambito → Dati. Source routing belongs to TRAMA, so
 *    this menu no longer exposes a separate "Full" mode.
 *  - Description copy tightened: every row is ICON + LABEL + 1-line
 *    helper + Switch. Same shape, scannable in <2s.
 *
 * SOTA peers (June 2026): ChatGPT model picker, Claude tools menu,
 * Cursor agent menu, Linear AI scope picker — all use a single flat
 * toggle list with clear plain-language labels and no mode subgroups.
 */

import { Crosshair, FlaskConical, Globe, Wrench } from "lucide-react";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";
import { Switch } from "../../primitives/switch";

export interface ToolsMenuProps {
  /** Currently-enabled toggles. Caller owns state. */
  webSearchMode: boolean;

  /** Toggle handlers. Caller wires these to the composer config slice. */
  onToggleWebSearch: (next: boolean) => void;

  /** @deprecated Public web search is available on every plan; ignored. */
  webSearchEnabled?: boolean;

  /**
   * v1 parity (May 23, 2026): CAD spatial-filter entry. The modal owner
   * lives outside the composer — pass `onOpenCad` and we fire it on click.
   * Omit the prop to hide the row entirely.
   */
  onOpenCad?: () => void;
  cadActive?: boolean;

  /**
   * "TERRA β" — experimental relational-RAG retrieval engine (Jul 2026,
   * admin-only). Corresponds to ``ConversationConfig.terraMode``; when ON
   * the send pipeline stamps ``retrieval_engine: "terra"`` on the WS
   * envelope (BE re-verifies the admin claim server-side). The caller owns
   * the admin gating AND counts the toggle into ``activeCount`` — no
   * role logic lives in this leaf package. Omit the handler to hide the
   * row entirely (e.g. non-admin sessions).
   */
  terraActive?: boolean;
  onToggleTerra?: (next: boolean) => void;
  tramaInfrastructure?: "current" | "lakehouse";
  onChangeTramaInfrastructure?: (next: "current" | "lakehouse") => void;
  /** True only from authenticated server readiness for the current scope. */
  tramaLakehouseAvailable?: boolean;

  /**
   * Total number of active tools in the accessible trigger label.
   * The visible indicator uses the enabled tools' icons.
   */
  activeCount?: number;

  /** Disable every interactive row (e.g. while a query is streaming). */
  disabled?: boolean;

  className?: string;
}

interface ToggleRowProps {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  label: string;
  hint?: string;
  active: boolean;
  disabled?: boolean;
  disabledHint?: string;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}

// May 28, 2026 — memoized so the parent ToolsMenu re-rendering doesn't
// cascade through every ToggleRow on every streaming chunk.
//
// June 11, 2026 — visual cleanup: chip-bg ``<span h-7 w-7 rounded-md
// bg-ink-1/[0.06]>`` attorno all'icona rimosso. Lo state attivo era
// segnalato DUE volte (chip arancione + Switch arancione) — single
// source of truth = Switch. L'icona nuda allinea anche i popover ai
// ScopeRow di Cartelle/File (icona nuda h-3.5 ink-3) → DRY visivo
// nell'intero stack dei popover composer. SOTA peers (ChatGPT Tools,
// Claude tools, Linear AI, Notion AI) hanno tutti icone nude nei
// tool menu — il chip-bg era un anti-pattern v1 da ripulire.
const ToggleRowImpl: React.FC<ToggleRowProps> = ({
  icon: Icon,
  label,
  hint,
  active,
  disabled,
  disabledHint,
  onChange,
  ariaLabel,
}) => (
  <div
    title={disabled ? disabledHint : hint}
    className={cn(
      "flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors",
      !disabled && "hover:bg-ink-1/[0.04]",
      disabled && "opacity-50",
    )}
  >
    <Icon
      aria-hidden
      size={16}
      strokeWidth={1.75}
      className={cn(
        "shrink-0 transition-colors",
        active ? "text-ando-orange" : "text-ink-2",
      )}
    />
    <span className="flex min-w-0 flex-1 flex-col leading-tight">
      <span className="font-medium text-[12px] text-ink-1">{label}</span>
      {hint ? <span className="text-[10.5px] text-ink-3">{hint}</span> : null}
    </span>
    <Switch
      checked={active}
      disabled={disabled}
      onCheckedChange={onChange}
      aria-label={ariaLabel}
    />
  </div>
);
ToggleRowImpl.displayName = "ToggleRow";
const ToggleRow = React.memo(ToggleRowImpl);

const ToolsMenuImpl: React.FC<ToolsMenuProps> = ({
  webSearchMode,
  onToggleWebSearch,
  onOpenCad,
  cadActive = false,
  terraActive = false,
  onToggleTerra,
  tramaInfrastructure = "current",
  onChangeTramaInfrastructure,
  tramaLakehouseAvailable = false,
  activeCount,
  disabled = false,
  className,
}) => {
  const { i18n } = useI18n();
  const count = activeCount ?? 0;
  const showBadge = count > 0;
  const lakehouseActive = tramaInfrastructure === "lakehouse";
  const anyActive = webSearchMode || cadActive || terraActive || showBadge;
  // Active tools surfaced as their own glyphs on the trigger (not a bare
  // count) — the user sees WHICH tools are on at a glance without the chip
  // strip. Subtle: small orange icons, no background.
  const activeTools = [
    { on: webSearchMode, id: "web", Icon: Globe },
    { on: cadActive, id: "cad", Icon: Crosshair },
    { on: terraActive, id: "terra", Icon: FlaskConical },
  ].filter((t) => t.on);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={showBadge ? i18n._(msg`Strumenti (${count} attivi)`) : i18n._(msg`Strumenti`)}
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-md px-2 font-medium text-[11.5px] text-ink-2 transition-colors hover:bg-ink-1/[0.04] hover:text-ink-1",
            anyActive && "text-accent",
            className,
          )}
        >
          <Wrench size={13} strokeWidth={1.75} aria-hidden />
          <span><Trans>Strumenti</Trans></span>
          {activeTools.length > 0 ? (
            <span aria-hidden className="flex items-center gap-0.5 text-ando-orange">
              {activeTools.map(({ id, Icon }) => (
                <Icon key={id} size={12} strokeWidth={2} />
              ))}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" sideOffset={8} className="w-[280px] p-1.5">
        {/* Flat list — June 11, 2026 IA pass. The previous "MODALITÀ
            DI RICERCA" / "TIPO DI RISPOSTA" section split implied two
            orthogonal axes the user had to coordinate. SOTA peers
            (ChatGPT, Claude, Cursor) keep the tools menu flat. */}
        <ToggleRow
          icon={Globe}
          label={i18n._(msg`Ricerca web`)}
          hint={i18n._(msg`Include risultati pubblici da internet`)}
          active={webSearchMode}
          disabled={disabled}
          onChange={onToggleWebSearch}
          ariaLabel={i18n._(msg`Attiva ricerca web`)}
        />
        {onOpenCad ? (
          // CAD spatial-filter entry — opens the dedicated modal (owner
          // lives outside the composer). It's an ACTION, not a toggle,
          // so the row is a button without a Switch on the right.
          // Icona nuda coerente con ToggleRow (giugno 11, 2026).
          <button
            type="button"
            onClick={onOpenCad}
            disabled={disabled}
            className={cn(
              "flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors",
              "hover:bg-ink-1/[0.04] disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <Crosshair
              aria-hidden
              size={16}
              strokeWidth={1.75}
              className={cn(
                "shrink-0 transition-colors",
                cadActive ? "text-ando-orange" : "text-ink-2",
              )}
            />
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="font-medium text-[12px] text-ink-1"><Trans>Analisi CAD</Trans></span>
              <span className="text-[10.5px] text-ink-3"><Trans>Filtra una porzione del disegno</Trans></span>
            </span>
          </button>
        ) : null}
        {onChangeTramaInfrastructure ? (
          <ToggleRow
            icon={FlaskConical}
            label={i18n._(msg`TRAMA Lakehouse`)}
            hint={!tramaLakehouseAvailable
              ? i18n._(msg`Lakehouse in preparazione`)
              : lakehouseActive
                ? i18n._(msg`Infrastruttura di ricerca`)
                : i18n._(msg`TRAMA attuale`)}
            active={lakehouseActive}
            disabled={disabled || (!tramaLakehouseAvailable && !lakehouseActive)}
            disabledHint={!tramaLakehouseAvailable ? i18n._(msg`Lakehouse in preparazione`) : undefined}
            onChange={(next) => {
              if (!next || tramaLakehouseAvailable) {
                onChangeTramaInfrastructure(next ? "lakehouse" : "current");
              }
            }}
            ariaLabel={i18n._(msg`TRAMA Lakehouse`)}
          />
        ) : onToggleTerra ? (
          // TERRA β — experimental retrieval engine, LAST row by design
          // (admin-only lab surface, below the customer-facing tools).
          // Icona nuda + Switch unica fonte di stato (giugno 11, 2026 —
          // niente chip-bg, anti-pattern rimosso).
          <ToggleRow
            icon={FlaskConical}
            label={i18n._(msg`TERRA β`)}
            hint={i18n._(msg`Motore di retrieval sperimentale (solo admin)`)}
            active={terraActive}
            disabled={disabled}
            onChange={onToggleTerra}
            ariaLabel={i18n._(msg`Attiva il motore di retrieval sperimentale TERRA`)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
ToolsMenuImpl.displayName = "ToolsMenu";

export const ToolsMenu = React.memo(ToolsMenuImpl);
